import clsx from 'clsx'
import EmptyResults from 'components/EmptyResults'
import GridSpinner from 'components/UI/GridSpinner'
import { parse } from 'date-fns'
import { Button, Col, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import format from 'utils/dateFns/format'
import formatRelative from 'utils/dateFns/formatRelative'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import StatusIcon from '../StatusIcon'
import styles from 'modules/Finance/PaymentProfiles/Invoices/components/List/payment-profiles-list.module.scss'
import downloadFile from 'utils/downloadFile'
import { APP_PRODUCT } from 'constants/app-config'

const List = ({ data = [], pagination, loading, onNextPage }) => {
  let groupDate = null
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()

  const today = `'${t('common::date_format.today')}'`
  const yesterday = `'${t('common::date_format.yesterday')}'`
  const formatDate = t('common::date_format.long')

  return (
    <div className={styles['list']}>
      {!loading && data.length === 0 && (
        <EmptyResults
          header={t('payment_profiles::payments.empty_list')}
          text={' '}
        />
      )}
      {data.map((invoice, i) => {
        let showDate = false
        const date = format(parse(invoice.created_at, 'yyyy-MM-dd', new Date()), 'dd')

        if (groupDate !== date) {
          showDate = true
          groupDate = date
        }

        return (
          <div key={'invoice-' + i}>
            {showDate && (
              <Typography
                variant={'Body-20'}
                noMargin
                className={styles['row__date']}
              >
                {formatRelative(
                  parse(invoice.created_at, 'yyyy-MM-dd', new Date()).getTime(),
                  `${formatDate}`,
                  {
                    today,
                    yesterday,
                  },
                )}
              </Typography>
            )}
            <Grid
              className={styles['row']}
              justifyContent={'space-between'}
              alignItems={'center'}
              gap={breakpoints.sm ? 12 : 26}
              wrap={false}
            >
              <Col>
                <StatusIcon
                  status={invoice.status}
                  breakpoints={breakpoints}
                />
              </Col>
              <Grid
                justifyContent={breakpoints.sm ? 'start' : 'space-between'}
                alignItems={breakpoints.sm ? 'start' : 'center'}
                direction={breakpoints.sm ? 'column' : 'row'}
                gap={6}
              >
                <Grid
                  direction={'column'}
                  fluid={false}
                  gap={6}
                >
                  <Typography
                    color={'Interface-Seventh'}
                    variant={breakpoints.sm ? 'Body-15' : 'Body-30'}
                    className={styles['row__title']}
                    noMargin
                  >
                    {invoice.invoice_number}
                  </Typography>
                  <Grid
                    fluid={false}
                    alignItems={'center'}
                  >
                    <Typography
                      variant={breakpoints.sm ? 'Body-10' : 'Body-15'}
                      component={'span'}
                      noMargin
                      color={'Label-Secondary'}
                    >
                      {t(`payment_profiles::payments.statuses.${invoice.status}`)}
                    </Typography>
                    {invoice.status !== 'new' && (
                      <>
                        <Icon
                          name={'dot'}
                          size={'s'}
                          color={'Label-Secondary'}
                        />
                        <Button
                          variant={'secondary'}
                          color={'Label-Secondary'}
                          colorHover={'Label-GU-Secondary'}
                          link
                          size={'s'}
                          endIcon={
                            <Icon
                              name={'download'}
                              width={16}
                            />
                          }
                          onClick={() =>
                            downloadFile(
                              `/api/app/v1/billing/invoices/${invoice.id}/document`,
                              `${APP_PRODUCT}-'invoice-'${invoice.invoice_number}`,
                            )
                          }
                        >
                          {t('payment_profiles::payments.download_pdf')}
                        </Button>
                      </>
                    )}
                  </Grid>
                </Grid>

                <Typography
                  noWrap
                  variant={breakpoints.sm ? 'p8' : 'p3'}
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(invoice.amount, invoice.currency),
                  }}
                />
              </Grid>
            </Grid>
          </div>
        )
      })}

      {data.length === 0 && loading && <GridSpinner />}

      {data.length > 0 && pagination.total !== data.length && (
        <div className={styles['pagination']}>
          <Grid
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Button
              onClick={() => onNextPage(1)}
              className={clsx(styles['show-more__button'])}
              variant={'link'}
              color={'Label-GU-Primary'}
              startIcon={
                loading ? (
                  <Spinner
                    width={16}
                    color={'inherit'}
                  />
                ) : null
              }
              endIcon={
                <Icon
                  name={'arrow1-down'}
                  color={'inherit'}
                  size={'s'}
                />
              }
            >
              {t('common::pagination.show_more')}
            </Button>
          </Grid>
        </div>
      )}
    </div>
  )
}

List.propTypes = {
  data: PropTypes.array,
  pagination: PropTypes.shape({
    total: PropTypes.number,
  }),
  loading: PropTypes.bool,
  onNextPage: PropTypes.func,
  i18n: PropTypes.string,
  profileId: PropTypes.string,
}

export default List
